import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function nextUrlToPath(nextUrl) {
  const raw = String(nextUrl || "").trim();
  if (!raw) return null;
  if (raw.startsWith("/")) return raw;
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return null;
  }
}

async function fetchAllPages(path, { maxPages = 10 } = {}) {
  const all = [];
  let next = path;
  let pages = 0;
  while (next && pages < maxPages) {
    // eslint-disable-next-line no-await-in-loop
    const data = await apiFetch(next, { method: "GET" });
    const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    all.push(...list);
    if (Array.isArray(data)) break;
    next = nextUrlToPath(data?.next);
    pages += 1;
  }
  return all;
}

export const fetchStores = createAsyncThunk("inventoryRevisions/fetchStores", async ({ search = "" } = {}) => {
  const qp = new URLSearchParams();
  qp.set("ordering", "name");
  if (String(search || "").trim()) qp.set("search", String(search || "").trim());
  const path = `/api/product/stores/?${qp.toString()}`;
  return await fetchAllPages(path, { maxPages: 10 });
});

export const fetchInventoryRevisions = createAsyncThunk(
  "inventoryRevisions/fetchInventoryRevisions",
  async ({ search = "", status = "" } = {}) => {
    const qp = new URLSearchParams();
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    if (String(status || "").trim()) qp.set("status", String(status || "").trim());
    const path = `/api/product/inventory-revisions/${qp.toString() ? `?${qp.toString()}` : ""}`;
    const data = await apiFetch(path, { method: "GET" });
    return Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
  }
);

export const fetchInventoryRevisionDetail = createAsyncThunk(
  "inventoryRevisions/fetchInventoryRevisionDetail",
  async ({ revisionId }) => {
    return await apiFetch(`/api/product/inventory-revisions/${revisionId}/`, { method: "GET" });
  }
);

export const fetchInventoryRevisionItems = createAsyncThunk(
  "inventoryRevisions/fetchInventoryRevisionItems",
  async ({ revisionId, barcode = "", productName = "" }) => {
    const qp = new URLSearchParams();
    if (String(barcode || "").trim()) qp.set("barcode", String(barcode || "").trim());
    if (String(productName || "").trim()) qp.set("product_name", String(productName || "").trim());
    const path = `/api/product/inventory-revisions/${revisionId}/items/${qp.toString() ? `?${qp.toString()}` : ""}`;
    const data = await apiFetch(path, { method: "GET" });
    return {
      revisionId,
      items: Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [],
    };
  }
);

export const createInventoryRevision = createAsyncThunk(
  "inventoryRevisions/createInventoryRevision",
  async ({ store, planned_finish_at, comment }) => {
    return await apiFetch("/api/product/inventory-revisions/", {
      method: "POST",
      body: { store, planned_finish_at, comment },
    });
  }
);

export const updateInventoryRevision = createAsyncThunk(
  "inventoryRevisions/updateInventoryRevision",
  async ({ revisionId, planned_finish_at, comment }) => {
    return await apiFetch(`/api/product/inventory-revisions/${revisionId}/`, {
      method: "PATCH",
      body: { planned_finish_at, comment },
    });
  }
);

export const createInventoryRevisionItem = createAsyncThunk(
  "inventoryRevisions/createInventoryRevisionItem",
  async ({ revisionId, product, expected_qty = "0", counted_qty, diff_reason, diff_comment }, { rejectWithValue }) => {
    try {
      const created = await apiFetch(`/api/product/inventory-revisions/${revisionId}/items/`, {
        method: "POST",
        body: { product, expected_qty, counted_qty, diff_reason, diff_comment },
      });
      return { revisionId, item: created };
    } catch (err) {
      // Preserve backend field errors shape, e.g. {"diff_comment":["This field may not be null."]}
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to create item" });
    }
  }
);

export const patchInventoryRevisionItem = createAsyncThunk(
  "inventoryRevisions/patchInventoryRevisionItem",
  async ({ revisionId, itemId, counted_qty, diff_reason, diff_comment }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/inventory-revisions/${revisionId}/items/${itemId}/`, {
        method: "PATCH",
        body: { counted_qty, diff_reason, diff_comment },
      });
      return { revisionId, item: updated };
    } catch (err) {
      // Preserve backend field errors shape, e.g. {"diff_comment":["This field may not be null."]}
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to save item" });
    }
  }
);

export const startInventoryRevision = createAsyncThunk(
  "inventoryRevisions/startInventoryRevision",
  async ({ revisionId }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/inventory-revisions/${revisionId}/to-inprogress/`, {
        method: "POST",
      });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to start revision" });
    }
  }
);

export const toCountedInventoryRevision = createAsyncThunk(
  "inventoryRevisions/toCountedInventoryRevision",
  async ({ revisionId }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/inventory-revisions/${revisionId}/to-counted/`, {
        method: "POST",
      });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to set revision to counted" });
    }
  }
);

export const toApprovedInventoryRevision = createAsyncThunk(
  "inventoryRevisions/toApprovedInventoryRevision",
  async ({ revisionId }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/inventory-revisions/${revisionId}/to-approved/`, {
        method: "POST",
      });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to approve revision" });
    }
  }
);

export const publishInventoryRevision = createAsyncThunk(
  "inventoryRevisions/publishInventoryRevision",
  async ({ revisionId }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/inventory-revisions/${revisionId}/publish/`, {
        method: "POST",
      });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to publish revision" });
    }
  }
);

export const cancelInventoryRevision = createAsyncThunk(
  "inventoryRevisions/cancelInventoryRevision",
  async ({ revisionId }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/inventory-revisions/${revisionId}/cancel/`, {
        method: "POST",
      });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to cancel revision" });
    }
  }
);

export const fetchProductByBarcode = createAsyncThunk(
  "inventoryRevisions/fetchProductByBarcode",
  async ({ barcode }, { rejectWithValue }) => {
    const code = String(barcode || "").trim();
    if (!code) return rejectWithValue({ barcode: ["Barcode is required"] });
    try {
      const data = await apiFetch(`/api/product/products/search-by-barcode/?barcode=${encodeURIComponent(code)}`, {
        method: "GET",
      });
      // Поддержка ответа в виде массива или { results: [...] }
      if (Array.isArray(data)) return data[0] ?? null;
      if (data && typeof data === "object" && Array.isArray(data.results)) return data.results[0] ?? null;
      return data;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to search product" });
    }
  }
);

export const fetchRevisionItemsByBarcode = createAsyncThunk(
  "inventoryRevisions/fetchRevisionItemsByBarcode",
  async ({ revisionId, barcode }, { rejectWithValue }) => {
    const code = String(barcode || "").trim();
    if (!code) return rejectWithValue({ barcode: ["Barcode is required"] });
    try {
      const data = await apiFetch(
        `/api/product/inventory-revisions/${revisionId}/items/?barcode=${encodeURIComponent(code)}`,
        { method: "GET" }
      );
      const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      return { revisionId, barcode: code, items: list };
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to search revision items" });
    }
  }
);

const slice = createSlice({
  name: "inventoryRevisions",
  initialState: {
    stores: {
      list: [],
      status: "idle",
      error: null,
      lastSearch: "",
    },
    revisions: {
      list: [],
      status: "idle",
      error: null,
      lastSearch: "",
    },
    revisionById: {},
    revisionStatusById: {},
    revisionErrorById: {},
    itemsByRevisionId: {},
    itemsStatusByRevisionId: {},
    itemsErrorByRevisionId: {},
    createStatus: "idle",
    createError: null,
    updateStatusById: {},
    updateErrorById: {},
    patchItemStatusById: {},
    patchItemErrorById: {},
    createItemStatusById: {},
    createItemErrorById: {},
    startStatusById: {},
    startErrorById: {},
    toCountedStatusById: {},
    toCountedErrorById: {},
    toApprovedStatusById: {},
    toApprovedErrorById: {},
    publishStatusById: {},
    publishErrorById: {},
    cancelStatusById: {},
    cancelErrorById: {},
    productByBarcode: {},
    productStatusByBarcode: {},
    productErrorByBarcode: {},
    revisionItemsByBarcode: {},
    revisionItemsStatusByBarcode: {},
    revisionItemsErrorByBarcode: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // stores
      .addCase(fetchStores.pending, (state, action) => {
        state.stores.status = "loading";
        state.stores.error = null;
        state.stores.lastSearch = action.meta.arg?.search || "";
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.stores.status = "succeeded";
        state.stores.list = action.payload || [];
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.stores.status = "failed";
        state.stores.error = action.error?.message || "Failed to load stores";
      })
      // revisions list
      .addCase(fetchInventoryRevisions.pending, (state, action) => {
        state.revisions.status = "loading";
        state.revisions.error = null;
        state.revisions.lastSearch = action.meta.arg?.search || "";
      })
      .addCase(fetchInventoryRevisions.fulfilled, (state, action) => {
        state.revisions.status = "succeeded";
        state.revisions.list = action.payload || [];
      })
      .addCase(fetchInventoryRevisions.rejected, (state, action) => {
        state.revisions.status = "failed";
        state.revisions.error = action.error?.message || "Failed to load revisions";
      })
      // revision detail
      .addCase(fetchInventoryRevisionDetail.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.revisionStatusById[id] = "loading";
        state.revisionErrorById[id] = null;
      })
      .addCase(fetchInventoryRevisionDetail.fulfilled, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.revisionStatusById[id] = "succeeded";
        state.revisionById[id] = action.payload;
      })
      .addCase(fetchInventoryRevisionDetail.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.revisionStatusById[id] = "failed";
        state.revisionErrorById[id] = action.error?.message || "Failed to load revision";
      })
      // revision items
      .addCase(fetchInventoryRevisionItems.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.itemsStatusByRevisionId[id] = "loading";
        state.itemsErrorByRevisionId[id] = null;
      })
      .addCase(fetchInventoryRevisionItems.fulfilled, (state, action) => {
        const id = String(action.payload.revisionId);
        state.itemsStatusByRevisionId[id] = "succeeded";
        state.itemsByRevisionId[id] = action.payload.items || [];
      })
      .addCase(fetchInventoryRevisionItems.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.itemsStatusByRevisionId[id] = "failed";
        state.itemsErrorByRevisionId[id] = action.error?.message || "Failed to load items";
      })
      // create revision
      .addCase(createInventoryRevision.pending, (state) => {
        state.createStatus = "saving";
        state.createError = null;
      })
      .addCase(createInventoryRevision.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        const created = action.payload;
        if (created?.id != null) state.revisionById[String(created.id)] = created;
      })
      .addCase(createInventoryRevision.rejected, (state, action) => {
        state.createStatus = "failed";
        state.createError = action.error?.message || "Failed to create revision";
      })
      // update revision
      .addCase(updateInventoryRevision.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.updateStatusById[id] = "saving";
        state.updateErrorById[id] = null;
      })
      .addCase(updateInventoryRevision.fulfilled, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.updateStatusById[id] = "succeeded";
        state.revisionById[id] = action.payload;
      })
      .addCase(updateInventoryRevision.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.updateStatusById[id] = "failed";
        state.updateErrorById[id] = action.error?.message || "Failed to update revision";
      })
      // patch item
      .addCase(patchInventoryRevisionItem.pending, (state, action) => {
        const key = `${action.meta.arg.revisionId}:${action.meta.arg.itemId}`;
        state.patchItemStatusById[key] = "saving";
        state.patchItemErrorById[key] = null;
      })
      .addCase(patchInventoryRevisionItem.fulfilled, (state, action) => {
        const { revisionId, item } = action.payload;
        const key = `${revisionId}:${item?.id}`;
        state.patchItemStatusById[key] = "succeeded";
        const rid = String(revisionId);
        const list = state.itemsByRevisionId[rid] || [];
        state.itemsByRevisionId[rid] = list.map((x) => (x.id === item.id ? item : x));
      })
      .addCase(patchInventoryRevisionItem.rejected, (state, action) => {
        const key = `${action.meta.arg.revisionId}:${action.meta.arg.itemId}`;
        state.patchItemStatusById[key] = "failed";
        state.patchItemErrorById[key] = action.error?.message || "Failed to save item";
      })
      // create item
      .addCase(createInventoryRevisionItem.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.createItemStatusById[id] = "saving";
        state.createItemErrorById[id] = null;
      })
      .addCase(createInventoryRevisionItem.fulfilled, (state, action) => {
        const { revisionId, item } = action.payload;
        const id = String(revisionId);
        state.createItemStatusById[id] = "succeeded";
        const list = state.itemsByRevisionId[id] || [];
        state.itemsByRevisionId[id] = [...list, item];
      })
      .addCase(createInventoryRevisionItem.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.createItemStatusById[id] = "failed";
        const payload = action.payload;
        state.createItemErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.error || payload.message)) ||
          action.error?.message ||
          "Failed to create item";
      })
      // start revision
      .addCase(startInventoryRevision.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.startStatusById[id] = "saving";
        state.startErrorById[id] = null;
      })
      .addCase(startInventoryRevision.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.revisionId);
        state.startStatusById[id] = "succeeded";
        if (updated) state.revisionById[id] = updated;
        // Best-effort: update list item if present
        state.revisions.list = (state.revisions.list || []).map((r) => (r.id === updated?.id ? updated : r));
      })
      .addCase(startInventoryRevision.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.startStatusById[id] = "failed";
        const payload = action.payload;
        state.startErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to start revision";
      })
      // to-counted
      .addCase(toCountedInventoryRevision.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.toCountedStatusById[id] = "saving";
        state.toCountedErrorById[id] = null;
      })
      .addCase(toCountedInventoryRevision.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.revisionId);
        state.toCountedStatusById[id] = "succeeded";
        if (updated) state.revisionById[id] = updated;
        state.revisions.list = (state.revisions.list || []).map((r) => (r.id === updated?.id ? updated : r));
      })
      .addCase(toCountedInventoryRevision.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.toCountedStatusById[id] = "failed";
        const payload = action.payload;
        state.toCountedErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to set revision to counted";
      })
      // to-approved
      .addCase(toApprovedInventoryRevision.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.toApprovedStatusById[id] = "saving";
        state.toApprovedErrorById[id] = null;
      })
      .addCase(toApprovedInventoryRevision.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.revisionId);
        state.toApprovedStatusById[id] = "succeeded";
        if (updated) state.revisionById[id] = updated;
        state.revisions.list = (state.revisions.list || []).map((r) => (r.id === updated?.id ? updated : r));
      })
      .addCase(toApprovedInventoryRevision.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.toApprovedStatusById[id] = "failed";
        const payload = action.payload;
        state.toApprovedErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to approve revision";
      })
      // publish
      .addCase(publishInventoryRevision.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.publishStatusById[id] = "saving";
        state.publishErrorById[id] = null;
      })
      .addCase(publishInventoryRevision.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.revisionId);
        state.publishStatusById[id] = "succeeded";
        if (updated) state.revisionById[id] = updated;
        state.revisions.list = (state.revisions.list || []).map((r) => (r.id === updated?.id ? updated : r));
      })
      .addCase(publishInventoryRevision.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.publishStatusById[id] = "failed";
        const payload = action.payload;
        state.publishErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to publish revision";
      })
      // cancel
      .addCase(cancelInventoryRevision.pending, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.cancelStatusById[id] = "saving";
        state.cancelErrorById[id] = null;
      })
      .addCase(cancelInventoryRevision.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.revisionId);
        state.cancelStatusById[id] = "succeeded";
        if (updated) state.revisionById[id] = updated;
        state.revisions.list = (state.revisions.list || []).map((r) => (r.id === updated?.id ? updated : r));
      })
      .addCase(cancelInventoryRevision.rejected, (state, action) => {
        const id = String(action.meta.arg.revisionId);
        state.cancelStatusById[id] = "failed";
        const payload = action.payload;
        state.cancelErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to cancel revision";
      })
      // product lookup
      .addCase(fetchProductByBarcode.pending, (state, action) => {
        const code = String(action.meta.arg?.barcode || "").trim();
        state.productStatusByBarcode[code] = "loading";
        state.productErrorByBarcode[code] = null;
      })
      .addCase(fetchProductByBarcode.fulfilled, (state, action) => {
        const code = String(action.meta.arg?.barcode || "").trim();
        state.productStatusByBarcode[code] = "succeeded";
        state.productByBarcode[code] = action.payload;
      })
      .addCase(fetchProductByBarcode.rejected, (state, action) => {
        const code = String(action.meta.arg?.barcode || "").trim();
        state.productStatusByBarcode[code] = "failed";
        const payload = action.payload;
        state.productErrorByBarcode[code] =
          (payload && typeof payload === "object" && (payload.detail || payload.error || payload.message)) ||
          action.error?.message ||
          "Failed to search product";
      })
      // revision item lookup by barcode (scoped by revision)
      .addCase(fetchRevisionItemsByBarcode.pending, (state, action) => {
        const rid = String(action.meta.arg?.revisionId);
        const code = String(action.meta.arg?.barcode || "").trim();
        const key = `${rid}:${code}`;
        state.revisionItemsStatusByBarcode[key] = "loading";
        state.revisionItemsErrorByBarcode[key] = null;
      })
      .addCase(fetchRevisionItemsByBarcode.fulfilled, (state, action) => {
        const rid = String(action.payload.revisionId);
        const code = String(action.payload.barcode || "").trim();
        const key = `${rid}:${code}`;
        state.revisionItemsStatusByBarcode[key] = "succeeded";
        state.revisionItemsByBarcode[key] = action.payload.items || [];
      })
      .addCase(fetchRevisionItemsByBarcode.rejected, (state, action) => {
        const rid = String(action.meta.arg?.revisionId);
        const code = String(action.meta.arg?.barcode || "").trim();
        const key = `${rid}:${code}`;
        state.revisionItemsStatusByBarcode[key] = "failed";
        const payload = action.payload;
        state.revisionItemsErrorByBarcode[key] =
          (payload && typeof payload === "object" && (payload.detail || payload.error || payload.message)) ||
          action.error?.message ||
          "Failed to search revision items";
      });
  },
});

export default slice.reducer;

// selectors
export const selectStores = (state) => state.inventoryRevisions.stores.list || [];
export const selectStoresStatus = (state) => state.inventoryRevisions.stores.status || "idle";
export const selectStoresError = (state) => state.inventoryRevisions.stores.error || null;

export const selectInventoryRevisions = (state) => state.inventoryRevisions.revisions.list || [];
export const selectInventoryRevisionsStatus = (state) => state.inventoryRevisions.revisions.status || "idle";
export const selectInventoryRevisionsError = (state) => state.inventoryRevisions.revisions.error || null;

export const selectRevisionById = (id) => (state) => state.inventoryRevisions.revisionById[String(id)] || null;
export const selectRevisionStatusById = (id) => (state) =>
  state.inventoryRevisions.revisionStatusById[String(id)] || "idle";
export const selectRevisionErrorById = (id) => (state) =>
  state.inventoryRevisions.revisionErrorById[String(id)] || null;

export const selectItemsByRevisionId = (id) => (state) => state.inventoryRevisions.itemsByRevisionId[String(id)] || [];
export const selectItemsStatusByRevisionId = (id) => (state) =>
  state.inventoryRevisions.itemsStatusByRevisionId[String(id)] || "idle";
export const selectItemsErrorByRevisionId = (id) => (state) =>
  state.inventoryRevisions.itemsErrorByRevisionId[String(id)] || null;

export const selectCreateRevisionStatus = (state) => state.inventoryRevisions.createStatus || "idle";
export const selectCreateRevisionError = (state) => state.inventoryRevisions.createError || null;

export const selectUpdateRevisionStatusById = (id) => (state) =>
  state.inventoryRevisions.updateStatusById[String(id)] || "idle";
export const selectUpdateRevisionErrorById = (id) => (state) =>
  state.inventoryRevisions.updateErrorById[String(id)] || null;

export const selectStartRevisionStatusById = (id) => (state) =>
  state.inventoryRevisions.startStatusById[String(id)] || "idle";
export const selectStartRevisionErrorById = (id) => (state) =>
  state.inventoryRevisions.startErrorById[String(id)] || null;

export const selectToCountedRevisionStatusById = (id) => (state) =>
  state.inventoryRevisions.toCountedStatusById[String(id)] || "idle";
export const selectToCountedRevisionErrorById = (id) => (state) =>
  state.inventoryRevisions.toCountedErrorById[String(id)] || null;

export const selectToApprovedRevisionStatusById = (id) => (state) =>
  state.inventoryRevisions.toApprovedStatusById[String(id)] || "idle";
export const selectToApprovedRevisionErrorById = (id) => (state) =>
  state.inventoryRevisions.toApprovedErrorById[String(id)] || null;

export const selectPublishRevisionStatusById = (id) => (state) =>
  state.inventoryRevisions.publishStatusById[String(id)] || "idle";
export const selectPublishRevisionErrorById = (id) => (state) =>
  state.inventoryRevisions.publishErrorById[String(id)] || null;

export const selectCancelRevisionStatusById = (id) => (state) =>
  state.inventoryRevisions.cancelStatusById[String(id)] || "idle";
export const selectCancelRevisionErrorById = (id) => (state) =>
  state.inventoryRevisions.cancelErrorById[String(id)] || null;

export const selectProductByBarcode = (barcode) => (state) =>
  state.inventoryRevisions.productByBarcode[String(barcode || "").trim()] || null;
export const selectProductStatusByBarcode = (barcode) => (state) =>
  state.inventoryRevisions.productStatusByBarcode[String(barcode || "").trim()] || "idle";
export const selectProductErrorByBarcode = (barcode) => (state) =>
  state.inventoryRevisions.productErrorByBarcode[String(barcode || "").trim()] || null;

export const selectRevisionItemsByBarcode = (revisionId, barcode) => (state) =>
  state.inventoryRevisions.revisionItemsByBarcode[`${String(revisionId)}:${String(barcode || "").trim()}`] || [];
export const selectRevisionItemsStatusByBarcode = (revisionId, barcode) => (state) =>
  state.inventoryRevisions.revisionItemsStatusByBarcode[`${String(revisionId)}:${String(barcode || "").trim()}`] ||
  "idle";
export const selectRevisionItemsErrorByBarcode = (revisionId, barcode) => (state) =>
  state.inventoryRevisions.revisionItemsErrorByBarcode[`${String(revisionId)}:${String(barcode || "").trim()}`] ||
  null;

export const selectCreateItemStatusById = (id) => (state) =>
  state.inventoryRevisions.createItemStatusById[String(id)] || "idle";
export const selectCreateItemErrorById = (id) => (state) =>
  state.inventoryRevisions.createItemErrorById[String(id)] || null;


